<template>
    <div class="row">
        <div class="col-md-6">
            <table class="table table-sm">
                <tr>
                    <td><strong>Номер заявки:</strong></td>
                    <td>{{ request.request_number }}</td>
                </tr>
                <tr>
                    <td><strong>Метки:</strong></td>
                    <td class="request-info-badges">
                        <RequestBadge
                            v-for="badge in requestBadges"
                            :key="badge.key"
                            :badge="badge"
                            class="mr-1 mb-1"
                        />
                    </td>
                </tr>
                <tr v-if="request.urgency_comment" class="red-row">
                    <td><strong>Причина срочности:</strong></td>
                    <td>{{ request.urgency_comment }}</td>
                </tr>
                <tr>
                    <td><strong>Тип заявки:</strong></td>
                    <td>
                        <i
                            class="request-info-type-icon"
                            :class="requestTypeIcon"
                            :title="requestTypeIconTitle"
                        ></i>
                        {{ request.RequestType ? request.RequestType.name : 'Не указан' }}
                    </td>
                </tr>
                <tr v-if="request.building_object_name || canEditRequestObject || objectChanged">
                    <td><strong>Объект:</strong></td>
                    <td>
                        <span>{{ request.building_object_name || 'Не указан' }}</span>
                        <b-button
                            v-if="canEditRequestObject"
                            :disabled="isAnyActionLoading"
                            variant="outline-secondary"
                            size="sm"
                            class="ml-2 request-info-edit-button"
                            title="Изменить объект"
                            aria-label="Изменить объект"
                            @click="openObjectModal"
                        >
                            <i class="fas fa-pencil-alt"></i>
                        </b-button>
                        <small v-if="objectChanged" class="d-block text-muted" role="status">Объект изменён · не сохранено</small>
                    </td>
                </tr>
                <tr v-if="request.building_contract_name">
                    <td><strong>Договор:</strong></td>
                    <td>{{ request.building_contract_name }}</td>
                </tr>
            </table>
        </div>
        <div class="col-md-6">
            <table class="table table-sm">
                <tr>
                    <td><strong>Создана:</strong></td>
                    <td>
                        <span>{{ formatDate(request.createdAt) }}</span>
                        <span v-if="createdElapsedText" class="request-info-relative">
                            · {{ createdElapsedText }}
                        </span>
                    </td>
                </tr>
                <tr v-if="request.deadline || canEditRequestDeadline" class="red-row">
                    <td><strong>Срок до:</strong></td>
                    <td>
                        <span>{{ request.deadline ? formatDate(request.deadline) : 'Не установлен' }}</span>
                        <span v-if="deadlineRelativeText" class="request-info-relative">
                            · {{ deadlineRelativeText }}
                        </span>
                        <b-button
                            v-if="canEditRequestDeadline"
                            variant="outline-secondary"
                            size="sm"
                            class="ml-2 request-info-edit-button"
                            title="Изменить срок"
                            aria-label="Изменить срок"
                            @click="openDeadlineModal"
                        >
                            <i class="fas fa-pencil-alt"></i>
                        </b-button>
                    </td>
                </tr>
                <tr v-if="request.completed_at">
                    <td><strong>Завершена:</strong></td>
                    <td>{{ formatDate(request.completed_at) }}</td>
                </tr>
            </table>
        </div>

        <RequestDeadlineModal v-model="showDeadlineModal" :request="request" :saving="deadlineSaving"
            :error="deadlineError" :can-edit="canEditRequestDeadline" @confirm="saveDeadline" />

        <b-modal content-class="argus-glass-dialog"
            v-model="showObjectModal"
            title="Объект заявки"
            centered
        >
            <b-form-group label="Объект" label-for="request-object-edit">
                <v-select
                    id="request-object-edit"
                    v-model="objectForm.building_process_id"
                    :options="objectOptions"
                    label="text"
                    :reduce="option => option.value"
                    :filterable="true"
                    :searchable="true"
                    :clearable="!requiresObject"
                    :disabled="objectOptionsLoading || isAnyActionLoading"
                    placeholder="Выберите объект"
                />
            </b-form-group>
            <small class="text-muted">Объект сохранится вместе с заявкой.</small>
            <template #modal-footer>
                <div class="w-100">
                    <b-alert v-if="objectError" show variant="danger" role="alert" class="mb-2">
                        {{ objectError }}
                        <b-button size="sm" variant="outline-danger" class="ml-2" :disabled="objectOptionsLoading"
                            @click="loadObjectOptions">Повторить загрузку</b-button>
                    </b-alert>
                    <div class="d-flex justify-content-end">
                        <b-button variant="secondary" @click="showObjectModal = false">Отмена</b-button>
                        <b-button variant="primary" class="ml-2" :disabled="!canSubmitObjectModal"
                            @click="applyObject">Применить</b-button>
                    </div>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import RequestDeadlineModal from '../../components/RequestDeadlineModal.vue';
import { 
    formatDate,
    formatDuration
} from '@/utils/requestCard/formatters';
import getRequestBadges from '@/utils/requestBadges';
import RequestBadge from '../../components/RequestBadge.vue';
import { isRequestOpened } from '@/utils/requestViewedStorage';
import { normalizeRequestTypeIcon, REQUEST_TYPE_ICON_OPTIONS } from '@/constants/requestTypeIcons';
import { isFinalStatus } from '@/utils/requestStatuses';
import { requestObjectRequired } from '@/utils/requestObjectRequired';

/**
 * Компонент отображения основной информации о заявке
 */
export default {
    name: 'RequestInfo',
    components: {
        RequestBadge, RequestDeadlineModal
    },
    props: {
        request: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            showDeadlineModal: false,
            deadlineSaving: false,
            deadlineError: '',
            showObjectModal: false,
            objectError: '',
            objectOptionsLoading: false,
            objectOptions: [],
            objectForm: {
                building_process_id: null
            },

        };
    },
    computed: {
        ...mapGetters('requestCard', [
            'canEditRequestObject',
            'isAnyActionLoading',
            'canEditRequestDeadline'
        ]),
        currentUser() {
            return this.$store.getters.currentUser;
        },
        requestBadges() {
            return getRequestBadges(this.request, this.$moment, {
                isNew: Boolean(this.currentUser && this.request && !isRequestOpened(this.request.id, this.currentUser))
            });
        },
        requestTypeWorkflowConfig() {
            const requestType = this.request && this.request.RequestType ? this.request.RequestType : null;
            return requestType && requestType.workflow_config ? requestType.workflow_config : {};
        },
        requestType() {
            return this.request && this.request.RequestType ? this.request.RequestType : {};
        },
        canSubmitObjectModal() {
            const id = this.normalizeObjectId(this.objectForm.building_process_id);
            return this.canEditRequestObject && !this.isAnyActionLoading && !this.objectOptionsLoading && !this.objectError &&
                (!this.requiresObject || !!id) && (!id || this.objectOptions.some(option => option.value === id)) && id !==
                this.normalizeObjectId(this.request.building_process_id);
        },
        requiresObject() {
            return requestObjectRequired(this.requestType);
        },
        objectChanged() {
            const original = this.$store.state.requestCard.originalRequest;
            return original && this.normalizeObjectId(this.request.building_process_id) !==
                this.normalizeObjectId(original.building_process_id);
        },
        requestTypeIcon() {
            return normalizeRequestTypeIcon(this.requestTypeWorkflowConfig.requestTypeIcon);
        },
        requestTypeIconTitle() {
            const icon = this.requestTypeIcon;
            const option = REQUEST_TYPE_ICON_OPTIONS.find(({ value }) => value === icon);
            return option ? option.text : 'Иконка типа заявки';
        },
        createdElapsedText() {
            const createdAt = this.parseDate(this.request.createdAt || this.request.created_at);
            if (!createdAt) {
                return '';
            }

            return `прошло ${formatDuration(Date.now() - createdAt.getTime())}`;
        },
        deadlineRelativeText() {
            const deadline = this.parseDate(this.request.deadline);
            if (!deadline || isFinalStatus(this.request.status)) {
                return '';
            }

            const diff = deadline.getTime() - Date.now();
            if (Math.abs(diff) < 60000) {
                return 'срок сейчас';
            }

            return diff >= 0
                ? `осталось ${formatDuration(diff)}`
                : `просрочено на ${formatDuration(Math.abs(diff))}`;
        }
    },
    methods: {
        ...mapActions('requestCard', [
            'requestCard_updateDeadline',
            'requestCard_fetchBuildingProcessOptions',
            'requestCard_setBuildingProcess'
        ]),
        formatDate,
        parseDate(value) {
            if (!value) {
                return null;
            }

            const date = new Date(value);
            return Number.isNaN(date.getTime()) ? null : date;
        },
        normalizeObjectId(value) {
            const parsed = Number(value);
            return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
        },
        formatObjectOption(process) {
            const value = this.normalizeObjectId(
                process && (process.value || process.building_process_id || process.id)
            );
            if (!value) {
                return null;
            }

            return {
                ...process,
                value,
                text: process.text ||
                    process.object_name ||
                    process.name ||
                    `Объект #${value}`
            };
        },
        async loadObjectOptions() {
            const requestId = this.request.id;
            this.objectOptionsLoading = true;
            this.objectError = '';
            try {
                const processes = await this.requestCard_fetchBuildingProcessOptions();
                if (this.request.id !== requestId) return;
                this.objectOptions = (Array.isArray(processes) ? processes : [])
                    .map(process => this.formatObjectOption(process))
                    .filter(Boolean);
            } catch (error) {
                if (this.request.id !== requestId) return;
                this.objectError = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Не удалось загрузить объекты';
                this.objectOptions = [];
            } finally {
                this.objectOptionsLoading = false;
            }
        },
        async openObjectModal() {
            this.objectForm = {
                building_process_id: this.normalizeObjectId(this.request.building_process_id)
            };
            this.showObjectModal = true;
            await this.loadObjectOptions();
        },
        toDateInputValue(value) {
            if (!value) {
                return null;
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return null;
            }

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        openDeadlineModal() {
            this.deadlineError = '';
            this.showDeadlineModal = true;
        },
        async saveDeadline({comment, ...payload}) {
            if (this.deadlineSaving) return;
            this.deadlineSaving = true;
            this.deadlineError = '';
            try {
                await this.requestCard_updateDeadline(payload);
                this.deadlineSaving = false;
                this.showDeadlineModal = false;
            } catch (error) {
                this.deadlineError = (error.response && error.response.data && error.response.data.message) || error.message || 'Не удалось изменить срок заявки';
            } finally { this.deadlineSaving = false; }
        },
        applyObject() {
            if (!this.canSubmitObjectModal) return;
            const nextValue = this.normalizeObjectId(this.objectForm.building_process_id);
            this.requestCard_setBuildingProcess(this.objectOptions.find(option => option.value === nextValue) || null);
            this.showObjectModal = false;
        }
    }
};
</script>

<style scoped>
.table td {
    border: none;
    padding: 0.25rem 0;
}

.table td:first-child {
    width: 40%;
}

.red-row td {
  background: rgba(220, 53, 69, 0.08); /* легкий красный фон */
}

.request-info-badges {
    display: flex;
    flex-wrap: wrap;
}

.request-info-type-icon {
    width: 1.1rem;
    margin-right: 0.35rem;
    color: #495057;
    text-align: center;
}

.request-info-edit-button {
    padding: 0.1rem 0.35rem;
    line-height: 1.1;
}

.request-info-relative {
    color: #6c757d;
    font-weight: 400;
}
</style>
